import SchemaOrg from '../../components/feature/SchemaOrg';
import StickyNav from './components/StickyNav';
import Hero from './components/Hero';
import Problem from './components/Problem';
import VoiceDemo from './components/VoiceDemo';
import PlatformDemo from './components/PlatformDemo';
import TheOffer from './components/TheOffer';
import Proof from './components/Proof';
import FinalCTA from './components/FinalCTA';
import StickyBottomBar from './components/StickyBottomBar';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://apex-emerald-ai.com';

const homeSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#localbusiness`,
    name: 'Apex Emerald AI',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/og-image.png`,
      width: 1200,
      height: 630,
    },
    image: `${siteUrl}/og-image.png`,
    description:
      'AI Voice Agents built for Irish trades businesses. Answer every call, qualify leads, and book jobs 24/7. Try free until your first job is booked.',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IE',
      addressLocality: 'Ireland',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 53.1424,
      longitude: -7.6921,
    },
    areaServed: [
      { '@type': 'Country', name: 'Ireland' },
      { '@type': 'Country', name: 'United Kingdom' },
    ],
    priceRange: 'Free trial',
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
        ],
        opens: '00:00',
        closes: '23:59',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      reviewCount: '3',
      bestRating: '5',
      worstRating: '1',
    },
    review: [
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: "Liam O'Sullivan" },
        reviewBody: "AI paid for itself month one. Revenue up 40% in 3 months.",
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        itemReviewed: { '@type': 'Service', name: 'Apex Emerald AI Voice Agent' },
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Jennifer Walsh' },
        reviewBody: "Team focuses on work. AI handles everything. Haven't missed a lead since.",
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        itemReviewed: { '@type': 'Service', name: 'Apex Emerald AI Voice Agent' },
      },
      {
        '@type': 'Review',
        author: { '@type': 'Person', name: 'Tom Davidson' },
        reviewBody: 'Live in a day. New jobs booked within a week.',
        reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
        itemReviewed: { '@type': 'Service', name: 'Apex Emerald AI Voice Agent' },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'How does the Apex Emerald AI free trial work?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You pay nothing until the AI books your first job. No card required, setup takes under 24 hours, and you get full access from day one.',
        },
      },
      {
        '@type': 'Question',
        name: 'What trades businesses can use Apex Emerald AI?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Apex Emerald AI is built for Irish and UK trades businesses including plumbers, electricians, HVAC technicians, roofers, and any tradesperson who takes inbound calls.',
        },
      },
      {
        '@type': 'Question',
        name: 'Does the AI work on WhatsApp, Instagram, and Facebook?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Apex Emerald AI covers voice calls, SMS, WhatsApp, Instagram DMs, and Facebook messages — all included in your setup.',
        },
      },
      {
        '@type': 'Question',
        name: 'How quickly can the AI voice agent be set up?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Setup takes under 24 hours. You get an onboarding call, full AI configuration, and integration with tools like Tradify, Jobber, and ServiceM8.',
        },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: 'Apex Emerald AI',
    url: siteUrl,
    description:
      'AI Voice Agents built for Irish trades businesses. Answer every call, qualify leads, and book jobs 24/7.',
    inLanguage: 'en-IE',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'Apex Emerald AI',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/og-image.png`,
      width: 1200,
      height: 630,
    },
    description:
      'Apex Emerald AI delivers AI Voice Agents for Irish trades businesses — answering calls, qualifying leads, and booking jobs around the clock.',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'IE',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      url: `${siteUrl}/contact`,
      availableLanguage: 'English',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Apex Emerald AI Voice Agent',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    description:
      'AI Voice Agent for Irish trades businesses. Answers every call 24/7, qualifies leads, and books jobs automatically.',
    url: siteUrl,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      description: 'Try free until your first job is booked — no card required.',
      availability: 'https://schema.org/InStock',
    },
    featureList: [
      '24/7 call answering',
      'Lead qualification',
      'Automatic job booking',
      'Multi-platform AI (voice, SMS, WhatsApp)',
      'Trades-specific setup',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'AI Voice Agent for Irish Trades',
    provider: {
      '@type': 'Organization',
      name: 'Apex Emerald AI',
      url: siteUrl,
    },
    areaServed: {
      '@type': 'Country',
      name: 'Ireland',
    },
    description:
      'Done-for-you AI voice agent setup for Irish plumbers, electricians, HVAC technicians, and other trades businesses. Answers every inbound call, qualifies the lead, and books the job — 24 hours a day.',
    serviceType: 'AI Voice Agent Automation',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      description: 'Free trial until first job booked',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${siteUrl}/#webpage`,
    url: siteUrl,
    name: 'Apex Emerald AI — AI Voice Agents for Irish Trades | Try Free Until You Book Your First Job',
    description:
      'Apex Emerald AI delivers AI Voice Agents built for Irish trades businesses. Answer every call, qualify leads, and book jobs 24/7. Try free until your first job is booked — no card required.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: { '@id': `${siteUrl}/#organization` },
    inLanguage: 'en-IE',
    datePublished: '2025-01-01',
    dateModified: '2026-04-12',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'h2', '.hero-headline'],
    },

    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl,
        },
      ],
    },
  },
];

export default function Home() {
  return (
    <main
      className="min-h-screen w-full font-dm"
      style={{ backgroundColor: '#080F0C', color: '#F5F5F5' }}
    >
      <SchemaOrg schemas={homeSchemas} />
      <StickyNav />

      <article>
        <section id="hero"><Hero /></section>
        <section id="problem"><Problem /></section>
        <section id="voice-demo"><VoiceDemo /></section>
        <section id="platform-demo"><PlatformDemo /></section>
        <section id="offer"><TheOffer /></section>
        <section id="proof"><Proof /></section>
        <section id="final-cta"><FinalCTA /></section>
      </article>

      <StickyBottomBar />
    </main>
  );
}
